// Mirrors Remotex.Core/Access/ResourceActions.cs. Endpoints return the caller's effective actions
// as `myActions`, so the UI gates on the same strings the backend enforces.

export const STORAGE_ACTIONS = {
  Read: "storage.read",
  ItemsWrite: "storage.items.write",
  ChestsWrite: "storage.chests.write",
  ConfigWrite: "storage.config.write",
  TasksRun: "storage.tasks.run",
  BotsAssign: "storage.bots.assign",
  AccessRead: "storage.access.read",
  AccessGrant: "storage.access.grant",
  AuditRead: "storage.audit.read",
  Delete: "storage.delete",
} as const;

export const BOT_ACTIONS = {
  Read: "bot.read",
  TasksRead: "bot.tasks.read",
  TasksRun: "bot.tasks.run",
  Control: "bot.control",
  WorldWrite: "bot.world.write",
  ConfigRead: "bot.config.read",
  ConfigWrite: "bot.config.write",
  MoneyRead: "bot.money.read",
  MoneyWrite: "bot.money.write",
  MsgsRead: "bot.msgs.read",
  MsgsSend: "bot.msgs.send",
  PossessView: "bot.possess.view",
  PossessControl: "bot.possess.control",
  AuditRead: "bot.audit.read",
  CredentialsManage: "bot.credentials.manage",
  ProxyAssign: "bot.proxy.assign",
  SurveillanceRead: "bot.surveillance.read",
  SurveillanceDelete: "bot.surveillance.delete",
  AccessRead: "bot.access.read",
  AccessGrant: "bot.access.grant",
  Delete: "bot.delete",
} as const;

export const PROXY_ACTIONS = {
  Read: "proxy.read",
  ConfigWrite: "proxy.config.write",
  Assign: "proxy.assign",
  AccessRead: "proxy.access.read",
  AccessGrant: "proxy.access.grant",
  Delete: "proxy.delete",
} as const;

export const PLAYERLIST_ACTIONS = {
  Read: "playerlist.read",
  EntriesWrite: "playerlist.entries.write",
  ConfigWrite: "playerlist.config.write",
  AccessRead: "playerlist.access.read",
  AccessGrant: "playerlist.access.grant",
  Delete: "playerlist.delete",
} as const;

export const WEBHOOK_ACTIONS = {
  Read: "webhook.read",
  Test: "webhook.test",
  ConfigWrite: "webhook.config.write",
  AccessRead: "webhook.access.read",
  AccessGrant: "webhook.access.grant",
  Delete: "webhook.delete",
} as const;

export interface RoleInfo {
  id: string;
  name: string;
  description: string;
  isBuiltIn: boolean;
  actions: string[];
}

export function can(actions: readonly string[] | undefined, action: string): boolean {
  return !!actions && actions.includes(action);
}

// Wire names are stable identifiers, not copy, so every one has a translated label. The key is
// derived rather than mapped, so a new backend action fails the locale coverage test instead of
// silently rendering as `bot.money.write` to a user.
export function actionLabelKey(action: string): string {
  return `ACTION_${action.replace(/\./g, "_").toUpperCase()}`;
}

// Built-in role names and descriptions are seeded in English by the backend. Their ids are fixed,
// so the UI translates by id and falls back to the stored name for tenant-defined roles, which are
// named by whoever created them and cannot be translated.
const BUILT_IN_ROLE_SLUGS: Readonly<Record<string, string>> = {
  "11111111-0000-4000-8000-000000000001": "STORAGE_READER",
  "11111111-0000-4000-8000-000000000002": "STORAGE_CONTRIBUTOR",
  "11111111-0000-4000-8000-000000000003": "STORAGE_OWNER",
  "11111111-0000-4000-8000-000000000004": "STORAGE_ACCESS_ADMIN",
  "11111111-0000-4000-8000-000000000005": "STORAGE_ADMIN",
  "22222222-0000-4000-8000-000000000001": "BOT_VIEWER",
  "22222222-0000-4000-8000-000000000002": "BOT_OPERATOR",
  "22222222-0000-4000-8000-000000000003": "BOT_OWNER",
  "22222222-0000-4000-8000-000000000004": "BOT_ACCESS_ADMIN",
  "22222222-0000-4000-8000-000000000005": "BOT_ADMIN",
  "33333333-0000-4000-8000-000000000001": "PROXY_USER",
  "33333333-0000-4000-8000-000000000002": "PROXY_OWNER",
  "44444444-0000-4000-8000-000000000001": "PLAYERLIST_READER",
  "44444444-0000-4000-8000-000000000002": "PLAYERLIST_CONTRIBUTOR",
  "44444444-0000-4000-8000-000000000003": "PLAYERLIST_OWNER",
  "55555555-0000-4000-8000-000000000001": "WEBHOOK_USER",
  "55555555-0000-4000-8000-000000000002": "WEBHOOK_OWNER",
  "11111111-0000-4000-8000-0000000000ff": "PLATFORM_ADMIN",
};

export function roleNameKey(roleId: string): string | null {
  const slug = BUILT_IN_ROLE_SLUGS[roleId.toLowerCase()];
  return slug ? `ROLE_${slug}` : null;
}

export function roleDescriptionKey(roleId: string): string | null {
  const slug = BUILT_IN_ROLE_SLUGS[roleId.toLowerCase()];
  return slug ? `ROLE_${slug}_DESC` : null;
}

// Actions worth a second look before granting: they move real currency, reach the Minecraft
// account itself, read private conversations, hand the grant power on, or destroy something.
// Referenced through the constants above so this list cannot name an action the backend dropped.
export const SENSITIVE_ACTIONS: ReadonlySet<string> = new Set<string>([
  // An arbitrary command includes /pay, so this is the money permission in practice, whatever the
  // catalog calls the others.
  BOT_ACTIONS.WorldWrite,
  BOT_ACTIONS.MoneyWrite,
  BOT_ACTIONS.CredentialsManage,
  BOT_ACTIONS.MsgsRead,
  BOT_ACTIONS.MsgsSend,
  BOT_ACTIONS.PossessControl,
  BOT_ACTIONS.SurveillanceRead,
  BOT_ACTIONS.SurveillanceDelete,
  BOT_ACTIONS.AccessGrant,
  BOT_ACTIONS.Delete,
  STORAGE_ACTIONS.AccessGrant,
  STORAGE_ACTIONS.Delete,
  PROXY_ACTIONS.AccessGrant,
  PROXY_ACTIONS.Delete,
  WEBHOOK_ACTIONS.AccessGrant,
  WEBHOOK_ACTIONS.Delete,
]);

// A badge needs one word for what is really a set of actions, so it names the highest tier the
// caller has reached rather than trying to name the role behind it.
export function botAccessLabelKey(actions: readonly string[] | undefined): string {
  if (can(actions, BOT_ACTIONS.Delete)) return "BOT_ACCESS_OWNER";
  if (can(actions, BOT_ACTIONS.AccessGrant)) return "BOT_ACCESS_ADMIN";
  if (can(actions, BOT_ACTIONS.Control)) return "BOT_ACCESS_OPERATOR";
  return "BOT_ACCESS_VIEWER";
}

export function playerListAccessLabelKey(actions: readonly string[] | undefined): string {
  if (can(actions, PLAYERLIST_ACTIONS.Delete)) return "PLAYERLIST_ACCESS_OWNER";
  if (can(actions, PLAYERLIST_ACTIONS.EntriesWrite)) return "PLAYERLIST_ACCESS_CONTRIBUTOR";
  return "PLAYERLIST_ACCESS_READER";
}
